/*
 * transform-json-types - v0.7.0
 * An utility to generate Flow, TypeScript, Rust Serde Struct and Scala Case Class from JSON.
 * https://github.com/transform-it/transform-json-types
 *
 * Made by Ritesh Kumar
 * Under MIT License
 */

(function (global, factory) {
	typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory(require('sha1'), require('lodash'), require('rust-keywords')) :
	typeof define === 'function' && define.amd ? define(['sha1', 'lodash', 'rust-keywords'], factory) :
	(global.TransformJsonTypes = factory(global.SHA1,global.lodash,global.rustReserved));
}(this, (function (SHA1,lodash,rustReserved) { 'use strict';

SHA1 = SHA1 && SHA1.hasOwnProperty('default') ? SHA1['default'] : SHA1;
rustReserved = rustReserved && rustReserved.hasOwnProperty('default') ? rustReserved['default'] : rustReserved;

/* eslint-disable standard/computed-property-even-spacing */
var typeNames = {
  STRING: "string",
  NUMBER: "number",
  INTEGER: "number",
  BOOLEAN: "boolean",
  ARRAY: "[]",
  ANY: "any"
};

var mapping = {
  flow: {
    interface: "type",
    separator: ",",
    startingBrace: "{",
    endingBrace: "}",
    terminator: ";",
    equator: " = ",
    types: typeNames,
    optional: "?",
    handleArray: function (className, any) {
      if ( className === void 0 ) className = "";

      return (any ? "any[]" : (className + "[]"));
}
  },
  typescript: {
    interface: "interface",
    separator: ";",
    startingBrace: "{",
    endingBrace: "}",
    terminator: "",
    equator: "",
    types: typeNames,
    optional: "?",
    handleArray: function (className, any) {
      if ( className === void 0 ) className = "";

      return (any ? "any[]" : (className + "[]"));
}
  },
  "rust-serde": {
    interface: "struct",
    separator: ",",
    startingBrace: "{",
    endingBrace: "}",
    terminator: "",
    equator: "",
    types: lodash.merge({}, typeNames, {
      STRING: "String",
      NUMBER: "f64",
      INTEGER: "i64",
      BOOLEAN: "bool",
      ANY: "()"
    }),
    handleOptionalValue: function (value) { return ("Option<" + value + ">"); },
    handleArray: function (className) {
      if ( className === void 0 ) className = "";

      return ("Vec<" + className + ">");
},
    preInterface: "#[derive(Serialize, Deserialize)]\n"
  },
  scala: {
    interface: "case class",
    separator: ",",
    startingBrace: "(",
    endingBrace: ")",
    terminator: "",
    equator: "",
    types: lodash.merge({}, typeNames, {
      STRING: "String",
      NUMBER: "Double",
      INTEGER: "Int",
      ANY: "Any",
      BOOLEAN: "Boolean"
    }),
    hideTerminatorAtLast: true,
    handleOptionalValue: function (value) { return ("Option[" + value + "]"); },
    handleArray: function (className) {
      if ( className === void 0 ) className = "";

      return ("Seq[" + className + "]");
}
  },
  "io-ts": {
    preText: "import * as t from 'io-ts'",
    interface: "const",
    separator: ",",
    startingBrace: "{",
    endingBrace: "})",
    terminator: ";",
    equator: " = t.type(",
    handleArray: function (className, any) {
        if ( className === void 0 ) className = "";

        return any ? "t.Array" : ("t.array(" + className + ")");
},
    types: {
      STRING: "t.string",
      NUMBER: "t.number",
      INTEGER: "t.Integer",
      ANY: "t.any",
      BOOLEAN: "t.boolean",
      ARRAY: "t.array"
    }
  },
  sarcastic: {
    interface: "const",
    separator: ",",
    startingBrace: "{",
    endingBrace: "})",
    terminator: ";",
    equator: " = is.shape(",
    handleArray: function (className, any) {
        if ( className === void 0 ) className = "";

        return any ? "is.array" : ("is.arrayOf(" + className + ")");
},
    handleOptionalValue: function (value) { return ("is.maybe(" + value + ")"); },
    types: {
      STRING: "is.string",
      NUMBER: "is.number",
      INTEGER: "is.number",
      ANY: "is.any",
      BOOLEAN: "is.boolean",
      ARRAY: "is.array"
    }
  }
};

var langDetails = {};
var classes = {};
var classesCache = {};
var classesInUse = {};
var optionalProperties = {};

function pascalCase(str) {
  return lodash.upperFirst(lodash.camelCase(str));
}

function setOptionalProperties(arr, objectName) {
  if (!isValueConsistent(arr)) { return; }
  var arrayOfKeys = arr.map(function (a) { return Object.keys(a); });
  optionalProperties[objectName] = lodash.difference(
    lodash.union.apply(void 0, arrayOfKeys),
    lodash.intersection.apply(void 0, arrayOfKeys)
  );
}

function hasSpecialChars(str) {
  return /[ ~`!@#$%\^&*+=\-\[\]\\';,\/{}|\\":<>\?]/g.test(str);
}

function getBasicType(value) {
  var types = langDetails.types;

  var type = types.STRING;
  switch (true) {
    case lodash.isString(value):
      type = types.STRING;
      break;
    case lodash.isInteger(value):
      type = types.INTEGER;
      break;
    case lodash.isNumber(value):
      type = types.NUMBER;
      break;
    case lodash.isBoolean(value):
      type = types.BOOLEAN;
      break;
  }
  return type;
}

function generateSignature(o) {
  if (lodash.isObject(o)) {
    return SHA1(Object.keys(o).map(function (n) { return n.toLowerCase(); }).sort().join("|"));
  } else {
    return SHA1(Object.keys(o).map(function (n) { return typeof n; }).sort().join("|"));
  }
}

function getValidClassName(key) {
  return pascalCase(key);
}

function getInterfaceType(key, value, classes, classesCache, classesInUse) {
  // get a valid className
  var className = getValidClassName(key);
  var currentObjectSignature = generateSignature(value);
  var isKnownClass =
    Object.keys(classesCache).indexOf(currentObjectSignature) !== -1;
  if (isKnownClass) { return classesCache[currentObjectSignature]; }
  if (classesInUse[className] !== undefined) {
    classesInUse[className]++;
    classesCache[currentObjectSignature] = className + classesInUse[className];
    return classesCache[currentObjectSignature];
  }
  classesCache[currentObjectSignature] = className;
  classesInUse[className] = 0;
  return className;
}

function isValueConsistent(arr) {
  if (!lodash.isEmpty(arr)) {
    arr.every(function (x) { return (lodash.isObject(x) ? "object" : typeof x); });
  }
  return true;
}

function analyzeObject(obj, objectName) {
  objectName = getInterfaceType(
    objectName,
    obj,
    classes,
    classesCache,
    classesInUse
  );
  classes[objectName] = classes[objectName] || {};

  Object.keys(obj).map(function (key) {
    var type = "string";
    var value = obj[key];
    var types = langDetails.types;
    var handleArray = langDetails.handleArray;

    switch (true) {
      case lodash.isString(value):
        type = types.STRING;
        break;
      case lodash.isInteger(value):
        type = types.INTEGER;
        break;
      case lodash.isNumber(value):
        type = types.NUMBER;
        break;
      case lodash.isBoolean(value):
        type = types.BOOLEAN;
        break;
      case lodash.isArray(value):
        type = handleArray("", true);
        if (isValueConsistent(value)) {
          if (lodash.isEmpty(value)) {
            type = handleArray("", true);
          } else {
            if (lodash.isObject(value[0])) {
              var clsName = getInterfaceType(
                key,
                lodash.assign.apply(void 0, [ {} ].concat( value )),
                classes,
                classesCache,
                classesInUse
              );
              type = handleArray(clsName);
              setOptionalProperties(value, clsName);
              analyzeObject(lodash.assign.apply(void 0, [ {} ].concat( value )), key);
            } else {
              type = "" + (handleArray(getBasicType(value[0])));
            }
          }
        }
        break;
      case lodash.isObject(value) && !lodash.isArray(value):
        type = types.ANY;
        if (!lodash.isEmpty(value)) {
          type = getInterfaceType(
            key,
            value,
            classes,
            classesCache,
            classesInUse
          );
          analyzeObject(value, key);
        }
        break;
    }
    if (hasSpecialChars(key)) {
      key = "\"" + key + "\"";
    }
    classes[objectName][key] = type;
  });

  return { classes: classes, classesCache: classesCache, classesInUse: classesInUse };
}

function isOptionalKey(key, objName) {
  return (
    optionalProperties[objName] && optionalProperties[objName].indexOf(key) >= 0
  );
}

function setOptional(key, objName) {
  if (isOptionalKey(key, objName) && langDetails.optional) {
    return langDetails.optional;
  }
  return "";
}

function rustRename(key, lang, clsName, rustCase) {
  var caseFunc = rustCase === "camelCase" ? lodash.camelCase : lodash.snakeCase;
  var casedText =
    hasSpecialChars(key) || rustCase === "snakeCase" ? caseFunc(key) : key;
  if (
    lang === "rust-serde" &&
    (rustReserved.indexOf(key) >= 0 ||
      key.indexOf(" ") >= 0 ||
      key !== casedText)
  ) {
    var changedKey = "" + (casedText.indexOf("_") >= 0 ? "" : "_") + casedText;
    return ("  #[serde(rename = \"" + (key.replace(/"/g, "")) + "\")]\n  " + changedKey);
  }
  return ("  " + key + (setOptional(key, clsName)));
}

function transform(obj, options) {
  obj = lodash.isString(obj) ? JSON.parse(obj) : obj;

  if (lodash.isArray(obj)) {
    obj = lodash.merge.apply(void 0, [ {} ].concat( obj ));
  }

  var defaultOptions = {
    objectName: "_RootInterface",
    lang: "flow",
    rustCase: "camelCase"
  };

  langDetails = {};
  optionalProperties = {};

  var ref = lodash.merge({}, defaultOptions, options);
  var objectName = ref.objectName;
  var lang = ref.lang;
  var rustCase = ref.rustCase;

  if (rustCase !== "camelCase" && rustCase !== "snakeCase") {
    throw new Error("rustCase can only be 'camelCase' or 'snakeCase'.");
  }

  langDetails = mapping[lang];
  var output = langDetails.preText ? ((langDetails.preText) + ";\n\n") : "";
  var localClasses = {};
  classes = {};
  classesCache = {};
  classesInUse = {};

  analyzeObject(obj, objectName);

  var equator = langDetails.equator;
  var separator = langDetails.separator;
  var endingBrace = langDetails.endingBrace;
  var startingBrace = langDetails.startingBrace;
  var terminator = langDetails.terminator;
  var preInterface = langDetails.preInterface;
  var hideTerminatorAtLast = langDetails.hideTerminatorAtLast;
  var handleOptionalValue = langDetails.handleOptionalValue;

  Object.keys(classes).map(function (clsName) {
    output = preInterface || "";
    output += (langDetails.interface) + " " + clsName + equator + " " + startingBrace + "\n";

    var keys = Object.keys(classes[clsName]);

    keys.map(function (key, i) {
      var _separator =
        i === keys.length - 1 && hideTerminatorAtLast ? "" : separator;
      var _key = rustRename(key, lang, clsName, rustCase);
      var _value = classes[clsName][key];
      _value =
        isOptionalKey(key, clsName) && handleOptionalValue
          ? handleOptionalValue(_value)
          : _value;

      output += _key + ": " + _value + _separator + "\n";
    });
    output += "" + endingBrace + terminator + "\n\n";
    localClasses[clsName] = output;
  });

  output = "";

  Object.keys(localClasses).sort().forEach(function (key) {
    output += localClasses[key];
  });

  return output;
}

return transform;

})));
